import { INIT_TIME_DATA_ITEM } from '../contants'
import {
  IMeanMidMode,
  ISMPRepAverSeasonPattern,
  ISeasonMapping,
  ISmpReferValue,
  ITimeDataItem,
  ITouPeriods,
  ITouRate,
} from './algoInterface'
import {
  classifyHourMonth,
  classifySeason,
  classifyWeekDay,
} from './classifyTimeData'

/**
 * 월별 시간별 평균 중앙 최빈값 계산
 * @param {ITimeDataItem[]}
 * @returns {IMeanMidMode}
 */

const calMonthRepPattern = (timeData: ITimeDataItem[]) => {
  const result: { [index: number]: any } = {}

  //월
  for (let month = 1; month < 13; month++) {
    // 시간
    for (let hour = 0; hour < 24; hour++) {
      const dataList = timeData
        .filter((item) => item.month === month && item.hour === hour)
        .map((item) => item.value)

      result[month][hour] = calMeanMidMode(dataList)
    }
  }

  return result
}

/**
 * number[]의 평균값, 중앙값, 최빈값 계산
 * @param {number []} numbers
 * @return
 */
export const calMeanMidMode = (numbers: number[]): IMeanMidMode => {
  const result = { mean: 0, mid: 0, mode: 0 }

  result.mean = numbers.reduce((acc, num) => acc + num, 0) / numbers?.length

  // 중앙값 계산
  const sortedNumbers = numbers.slice().sort((a, b) => a - b)
  const middleIndex = Math.floor(sortedNumbers?.length / 2)
  result.mid =
    sortedNumbers?.length % 2 === 0
      ? (sortedNumbers[middleIndex - 1] + sortedNumbers[middleIndex]) / 2
      : sortedNumbers[middleIndex]

  // 최빈값 계산 (소수점 반올림)
  // [1,1,2,2,2,3,3,3]

  const counts: { [key: number]: number } = {}
  for (const num of numbers) {
    const roundedNum: number = Number(num.toFixed(2))
    counts[roundedNum] = (counts[roundedNum] || 0) + 1
  }
  // counts = {1:2,2:3,3:4}
  let maxFrequency = 0
  let isModeDup = false

  for (const roundedNum in counts) {
    const frequency = counts[roundedNum]
    if (frequency > maxFrequency) {
      maxFrequency = frequency
      isModeDup = false
      result.mode = Number(roundedNum)
    } else if (frequency === maxFrequency) {
      isModeDup = true
    }
  }

  //최빈값이 없으면 평균값 사용
  if (isModeDup) {
    result.mode = result.mean
  }

  return result
}

/**
 * smp 로 계절별 + 근휴무일별 대표패턴(평균값) 구하기
 * @param {ITimeDataItem[]} data - Time 데이터 (발전/부하)
 * @param {ISeasonMapping} seasonMapping - 월별 계절
 * @param {IholidayData} holidayData - 공휴일
 */
export const calSeasonalWeekdayAverSMP = (
  data: ITimeDataItem[],
  seasonMapping: ISeasonMapping,
  holidayData: number[]
): ISMPRepAverSeasonPattern => {
  const result: ISMPRepAverSeasonPattern = {
    spr: {
      week: Array(24).fill(0),
      sat: Array(24).fill(0),
      holi: Array(24).fill(0),
    },
    sum: {
      week: Array(24).fill(0),
      sat: Array(24).fill(0),
      holi: Array(24).fill(0),
    },
    fall: {
      week: Array(24).fill(0),
      sat: Array(24).fill(0),
      holi: Array(24).fill(0),
    },
    win: {
      week: Array(24).fill(0),
      sat: Array(24).fill(0),
      holi: Array(24).fill(0),
    },
  }

  let timeData: ITimeDataItem[] = classifySeason(data, seasonMapping)
  timeData = classifyWeekDay(timeData, holidayData)
  if (!timeData[0].hour) {
    timeData = classifyHourMonth(timeData)
  }

  for (let season in result) {
    //@ts-ignore
    for (let workDay in result[season]) {
      for (let hour = 0; hour < 24; hour++) {
        const filteredSMP = timeData.filter(
          (v) => v.hour === hour && v.season === season && v.workDay === workDay
        )

        filteredSMP.forEach((v) => {
          //@ts-ignore
          result[season][workDay][hour] += v.value
        })

        //@ts-ignore
        result[season][workDay][hour] /= filteredSMP?.length
      }
    }
  }

  return result
}

/**
 * 계절별,근휴무일별 대표패턴 평균내서 계절별 대표패턴 구하기
 * @param {ISMPRepAverSeasonPattern} data - 계절별,근휴무일별 대표패턴
 * @returns 계절별 대표패턴
 */
const calSeasonalAverSMP = (
  data: ISMPRepAverSeasonPattern
): { spr: number[]; sum: number[]; fall: number[]; win: number[] } => {
  const result = {
    spr: Array(24).fill(0),
    sum: Array(24).fill(0),
    fall: Array(24).fill(0),
    win: Array(24).fill(0),
  }

  for (let season of Object.keys(result)) {
    for (let index = 0; index < 24; index++) {
      const average =
        //@ts-ignore
        (data[season]['week'][index] +
          //@ts-ignore
          data[season]['sat'][index] +
          //@ts-ignore
          data[season]['holi'][index]) /
        3
      //@ts-ignore
      result[season][index] = average
    }
  }
  return result
}

/**
 * 백분위로 33.3 66.6 계산
 * @param data
 * @param percentile
 * @returns
 */
export const findUpperPercentileValue = (
  data: number[],
  percentile: number
): number => {
  // 평균 계산
  const mean = data.reduce((acc, val) => acc + val, 0) / data?.length

  // 표준편차 계산
  const stdDeviation = Math.sqrt(
    data.reduce((acc, val) => acc + Math.pow(val - mean, 2), 0) / data?.length
  )

  // Z-통계량 계산
  const zScore = ((percentile - 50) / 100) * Math.sqrt(2) // 정규분포 상에서의 Z-score

  // 백분위 상위 해당하는 값 계산
  const upperValue = mean + zScore * stdDeviation

  return upperValue
}

/**
 * 계절별, 근 휴무별 33.3% / 66.6% 되는 수 구하기
 * @param {ISMPRepAverSeasonPattern} averSMP -계절별, 근휴무별 smp 대표값
 * @returns 계절별, 근휴무별 33.3 66.6 지점값
 */
export const calSmpReferValue = (
  averSMP: ISMPRepAverSeasonPattern
): ISmpReferValue => {
  const result: ISmpReferValue = {
    spr: {
      week: {
        large: 0,
        small: 0,
      },
      sat: {
        large: 0,
        small: 0,
      },
      holi: {
        large: 0,
        small: 0,
      },
    },
    sum: {
      week: {
        large: 0,
        small: 0,
      },
      sat: {
        large: 0,
        small: 0,
      },
      holi: {
        large: 0,
        small: 0,
      },
    },
    fall: {
      week: {
        large: 0,
        small: 0,
      },
      sat: {
        large: 0,
        small: 0,
      },
      holi: {
        large: 0,
        small: 0,
      },
    },
    win: {
      week: {
        large: 0,
        small: 0,
      },
      sat: {
        large: 0,
        small: 0,
      },
      holi: {
        large: 0,
        small: 0,
      },
    },
  }

  const seasonList = ['spr', 'sum', 'fall', 'win']
  const holiList = ['week', 'sat', 'holi']

  for (let season of seasonList) {
    for (let holi of holiList) {
      //큰 값이 66.6 이고 작은 값이 33.3임! 정규분포라서!!!

      //@ts-ignore
      result[season][holi]['large'] = findUpperPercentileValue(
        //@ts-ignore
        averSMP[season][holi],
        66.6
      )
      //@ts-ignore
      result[season][holi]['small'] = findUpperPercentileValue(
        //@ts-ignore
        averSMP[season][holi],
        33.3
      )
    }
  }

  return result
}

/**
 * smp와 기준값 넣으면 시간대와 비율 계산
 * @param numbers {}
 * @param large
 * @param small
 * @returns
 */
const calRangeAndRate = (
  numbers: number[],
  large: number,
  small: number
): { result: string[]; minMid: number; minMax: number } => {
  let maxCount = 0
  let midCount = 0
  let minCount = 0
  let maxSum = 0
  let midSum = 0
  let minSum = 0

  let result: string[] = []

  for (let num of numbers) {
    if (num > large) {
      maxCount++
      maxSum += num
      result.push('max')
    } else if (num < small) {
      minCount++
      minSum += num
      result.push('min')
    } else {
      midCount++
      midSum += num
      result.push('mid')
    }
  }

  const maxAverage = maxCount > 0 ? maxSum / maxCount : 0
  const midAverage = midCount > 0 ? midSum / midCount : 0
  const minAverage = minCount > 0 ? minSum / minCount : 0

  let minMid = minAverage !== 0 ? midAverage / minAverage : 1
  let minMax = minAverage !== 0 ? maxAverage / minAverage : 1

  minMid = Number(minMid.toFixed(3))
  minMax = Number(minMax.toFixed(3))
  return { result: result, minMid: minMid, minMax: minMax }
}

// 계절별, 근휴무일별 smp로 시간대 계산
/**
 *
 * @param {ISMPRepAverSeasonPattern} smpData -계절별, 근휴무일별 smp
 * @param {ISmpReferValue} smpReferValue - 계절별, 근휴무일별 smp 33.3 66.6 값
 * @returns 계절별 근휴무일별 시간대
 */
const getSmpTime = (
  smpData: ISMPRepAverSeasonPattern,
  smpReferValue: ISmpReferValue
): ITouPeriods => {
  let result: ITouPeriods = {
    spr: {
      week: [],
      sat: [],
      holi: [],
    },
    sum: {
      week: [],
      sat: [],
      holi: [],
    },
    fall: {
      week: [],
      sat: [],
      holi: [],
    },
    win: {
      week: [],
      sat: [],
      holi: [],
    },
  }

  for (let season of Object.keys(smpData)) {
    for (let weekDay of ['week', 'sat', 'holi']) {
      const touPeriods = calRangeAndRate(
        //@ts-ignore
        smpData[season][weekDay],
        //@ts-ignore
        smpReferValue[season][weekDay].large,
        //@ts-ignore
        smpReferValue[season][weekDay].small
      )

      //@ts-ignore
      result[season][weekDay] = touPeriods.result
    }
  }

  return result
}

// 계절별 비율 구하기
/**
 *
 * @param  {spr: number[], sum: number[], fall: number[], win: number[]} seasonalAverSMP - 계절별 smp 기본값
 * @param {ISmpReferValue} smpReferValue - 계절별, 근휴무일별 smp 기준값 (33.3/66.6)
 */
const getSmpRate = (
  seasonalAverSMP: {
    spr: number[]
    sum: number[]
    fall: number[]
    win: number[]
  },
  smpReferValue: ISmpReferValue
): ITouRate => {
  const result: ITouRate = {
    spr: {
      minMid: 0,
      minMax: 0,
    },
    sum: {
      minMid: 0,
      minMax: 0,
    },
    fall: {
      minMid: 0,
      minMax: 0,
    },
    win: {
      minMid: 0,
      minMax: 0,
    },
  }
  //계절별 기준값 평균내기
  const sprSmall =
    (smpReferValue.spr.holi.small +
      smpReferValue.spr.sat.small +
      smpReferValue.spr.week.small) /
    3
  const sprLarge =
    (smpReferValue.spr.holi.large +
      smpReferValue.spr.sat.large +
      smpReferValue.spr.week.large) /
    3
  const sumSmall =
    (smpReferValue.sum.holi.small +
      smpReferValue.sum.sat.small +
      smpReferValue.sum.week.small) /
    3
  const sumLarge =
    (smpReferValue.sum.holi.large +
      smpReferValue.sum.sat.large +
      smpReferValue.sum.week.large) /
    3
  const fallSmall =
    (smpReferValue.fall.holi.small +
      smpReferValue.fall.sat.small +
      smpReferValue.fall.week.small) /
    3
  const fallLarge =
    (smpReferValue.fall.holi.large +
      smpReferValue.fall.sat.large +
      smpReferValue.fall.week.large) /
    3
  const winSmall =
    (smpReferValue.win.holi.small +
      smpReferValue.win.sat.small +
      smpReferValue.win.week.small) /
    3
  const winLarge =
    (smpReferValue.win.holi.large +
      smpReferValue.win.sat.large +
      smpReferValue.win.week.large) /
    3
  // 비율 구하기
  const sprRate = calRangeAndRate(seasonalAverSMP.spr, sprLarge, sprSmall)
  result.spr.minMid = sprRate.minMid
  result.spr.minMax = sprRate.minMax

  const sumRate = calRangeAndRate(seasonalAverSMP.sum, sumLarge, sumSmall)
  result.sum.minMid = sumRate.minMid
  result.sum.minMax = sumRate.minMax

  const fallRate = calRangeAndRate(seasonalAverSMP.fall, fallLarge, fallSmall)
  result.fall.minMid = fallRate.minMid
  result.fall.minMax = fallRate.minMax

  const winRate = calRangeAndRate(seasonalAverSMP.win, winLarge, winSmall)
  result.win.minMid = winRate.minMid
  result.win.minMax = winRate.minMax

  //계절별 비율 return
  return result
}

// 총
export const calSmpTimeRate = (
  data: ITimeDataItem[],
  seasonMapping: ISeasonMapping,
  holidayData: number[]
): { smpTimePeriod: any; smpRate: ITouRate } => {
  // 계절별,근휴무별 대표패턴 만들기
  const smpRepPattern = calSeasonalWeekdayAverSMP(
    data,
    seasonMapping,
    holidayData
  )

  // 33.3%, 66.6% 값 구하기
  const smpReferValue = calSmpReferValue(smpRepPattern)

  // 시간대 구하기
  const smpTimePeriod = getSmpTime(smpRepPattern, smpReferValue)

  // 계절별로 평균 구해서 시간대별 평균내기
  const seasonalAverSMP = calSeasonalAverSMP(smpRepPattern)

  // 계절별 비율 구하기
  const smpRate = getSmpRate(seasonalAverSMP, smpReferValue)

  return { smpTimePeriod: smpTimePeriod, smpRate: smpRate }
}

//smp 데이터 들어온거 옵션에 따라 value로 바꿔주기
export const transExSmpToValue = (
  data: any[],
  opt: string
): ITimeDataItem[] => {
  if (opt === 'maxSmp') {
    return data.map((item) => ({
      date: item.date,
      value: item.maxSmp,
    })) as ITimeDataItem[]
  } else if (opt === 'minSmp') {
    return data.map((item) => ({
      date: item.date,
      value: item.minSmp,
    })) as ITimeDataItem[]
  } else if (opt === 'weightedAver') {
    return data.map((item) => ({
      date: item.date,
      value: item.weightedAver,
    })) as ITimeDataItem[]
  } else {
    return [INIT_TIME_DATA_ITEM]
  }
}
